import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Activity,
  FileText,
  Eye,
  Sparkles,
} from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";
import { heroFadeUp, heroScaleIn } from "@/lib/animations";

const floatingTags = [
  { label: "Risco qualificado", icon: ShieldCheck, color: "#3532B3" },
  { label: "Parecer técnico", icon: FileText, color: "#3787F5" },
  { label: "Rastreabilidade", icon: Eye, color: "#12DDB5" },
  { label: "Gestão da carteira", icon: Activity, color: "#3787F5" },
];

const HeroContent = () => (
  <motion.div {...heroFadeUp}>
    <span
      data-testid="hero-tag"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EAF2] shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-xs font-bold uppercase tracking-[0.18em] text-[#3787F5]"
    >
      <Sparkles size={14} className="text-[#12DDB5]" strokeWidth={2.4} />
      Healthtech para saúde suplementar
    </span>

    <h1
      data-testid="hero-headline"
      className="mt-7 text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl font-extrabold text-[#1F2937] tracking-[-0.025em]"
    >
      Decisões mais <span className="text-gradient-brand">seguras</span> para
      uma carteira mais previsível
    </h1>

    <p
      data-testid="hero-subheadline"
      className="mt-6 text-lg text-[#64748B] leading-relaxed max-w-xl"
    >
      A Previsa Saúde combina entrevista qualificada, análise técnica e
      inteligência de dados para apoiar operadoras e administradoras na
      qualificação de risco desde a adesão.
    </p>

    <div className="mt-9 flex flex-col sm:flex-row gap-4">
      <a
        data-testid="hero-cta-primary"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-[#3532B3] to-[#3787F5] text-white font-semibold shadow-[0_12px_28px_rgba(53,50,179,0.28)] hover:shadow-[0_14px_36px_rgba(53,50,179,0.38)] hover:-translate-y-0.5 transition-all duration-300"
      >
        Fale com a Previsa
        <ArrowRight size={18} strokeWidth={2.2} />
      </a>
      <a
        data-testid="hero-cta-secondary"
        href="#solucoes"
        className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-white text-[#1F2937] font-semibold border border-[#E5EAF2] hover:border-[#3787F5] hover:text-[#3532B3] hover:bg-[#F6F8FB] transition-all duration-300"
      >
        Conheça as soluções
      </a>
    </div>

    <p className="mt-5 text-sm text-[#64748B]">
      Entrevista qualificada, segurança técnica e inteligência de carteira.
    </p>
  </motion.div>
);

const HeroVisual = () => (
  <motion.div {...heroScaleIn} className="relative">
    <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(53,50,179,0.18)] border border-white">
      <img
        src="https://customer-assets.emergentagent.com/job_saude-decisoes/artifacts/jbur9cha_sac-previsa.png"
        alt="Atendimento qualificado por enfermeira da Previsa Saúde"
        className="w-full h-[460px] md:h-[520px] object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#3532B3]/45 via-[#3787F5]/10 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#3532B3]/25" />

      <div className="absolute top-6 left-6 right-6 flex justify-between gap-3 pointer-events-none">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl px-4 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.12)] border border-white">
          <div className="text-[10px] tracking-[0.16em] font-bold text-[#64748B] uppercase">
            Entrevistas / mês
          </div>
          <div className="text-2xl font-extrabold text-[#1F2937] mt-1">
            6.000
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl px-4 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.12)] border border-white">
          <div className="text-[10px] tracking-[0.16em] font-bold text-[#64748B] uppercase">
            Tempo médio
          </div>
          <div className="text-2xl font-extrabold text-[#1F2937] mt-1">
            15<span className="text-sm text-[#64748B] ml-1">min</span>
          </div>
        </div>
      </div>
    </div>

    <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-2xl shadow-[0_18px_40px_rgba(15,23,42,0.12)] border border-[#E5EAF2] p-4 float-soft hidden sm:block">
      <div className="grid grid-cols-2 gap-3 w-[260px]">
        {floatingTags.map((t) => (
          <div
            key={t.label}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F6F8FB]"
          >
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${t.color}1A`, color: t.color }}
            >
              <t.icon size={14} strokeWidth={2.2} />
            </span>
            <span className="text-[11px] font-semibold text-[#1F2937] leading-tight">
              {t.label}
            </span>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute -top-4 -right-2 md:-right-4 bg-gradient-to-r from-[#12DDB5] to-[#73F2B3] rounded-2xl shadow-[0_18px_40px_rgba(18,221,181,0.35)] px-4 py-3 float-soft-delay hidden sm:flex items-center gap-2">
      <ShieldCheck size={18} className="text-[#0b5a4a]" strokeWidth={2.4} />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#0b5a4a]">
          Risco
        </div>
        <div className="text-sm font-extrabold text-[#0b5a4a] leading-none mt-0.5">
          Qualificado
        </div>
      </div>
    </div>
  </motion.div>
);

export const Hero = () => (
  <section
    id="inicio"
    data-testid="hero-section"
    className="relative pt-32 md:pt-40 pb-10 md:pb-12 overflow-hidden"
  >
    <div className="blob w-[480px] h-[480px] -top-32 -left-32 bg-[#3787F5]/30" />
    <div className="blob w-[420px] h-[420px] top-20 right-0 bg-[#12DDB5]/25" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(53,50,179,0.06)_0%,_transparent_60%)] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
      <HeroContent />
      <HeroVisual />
    </div>
  </section>
);
